import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Select,
    TablePagination,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import { ArrowDropDown, OpenInNew } from '@material-ui/icons';
import React from 'react';
import DocumentTitle from 'react-document-title';
import { ProfileConflictAction, ProfileConflictActivity } from 'shared/models/profile-conflict-review';

import { startProfileConflictInvestigation } from '../api';
import { fullDateTime } from '../common/timestamp';
import { config } from '../config';
import { ProfileConflictResolver } from '../containers/profile-conflict-resolver';
import {
    MarkProfileConflictReviewed,
    ProfileConflictActivity as ProfileConflictActivityQuery,
    ProfileConflictActivityDetails,
    ProfileConflictActivityUsers,
    ProfileConflictHistoryRecords
} from '../graphql/queries/profile-conflicts';
import { useAgent } from '../hooks/use-agent';
import { useSlides } from '../hooks/use-candidate-slides';
import { useModal } from '../hooks/use-modal';
import { useSnackbar } from '../hooks/use-snackbar';
import { CellValue, DataPanel, DataPanelColumn, dataPanelContainerStyles, selectorStyles } from './data-panel';
import { Header } from './header';
import { NavigationSelect } from './navigation-select';

type Column = 'profiles' | 'outcome' | 'resolvedBy' | 'reviewedBy' | 'date';

interface ConflictDetails extends ProfileConflictActivity {
    review?: {
        history: ProfileConflictAction[];
        merges: Array<Omit<ProfileConflictAction, 'kind'>>;
    };
}

const pageSize = 50;
const pollIntervalMs = 10000;
const viewRoutes: Array<[string, string]> = [
    ['Needs resolution', '/profile-conflicts'],
    ['Review agent work', '/profile-conflicts/review'],
    ['Agent processing', '/profile-conflicts/processing'],
    ['History', '/profile-conflicts/history']
];
const labels: Record<string, string> = {
    merge: 'Merged',
    resolve: 'Conflict resolved',
    transfer: 'Data transferred',
    transfer_contact: 'Contact transferred',
    transfer_file: 'File transferred',
    transfer_profile_url: 'Profile URL transferred',
    unsure: 'Needs human decision'
};

const getProfiles = (row: ProfileConflictActivity) =>
    [
        { id: row.personId1, name: row.personName1 },
        { id: row.personId2, name: row.personName2 }
    ].filter((profile) => profile.name && profile.name !== profile.id);

const outcomeLabel = (row: ProfileConflictActivity) => {
    if (row.status === 'pending') return 'Agent processing';
    if (row.status === 'failed') return 'Investigation failed';
    return labels[row.outcome] || 'Not investigated';
};

const getCellValue =
    (column: Column) =>
    (row: ProfileConflictActivity): CellValue => {
        switch (column) {
            case 'profiles':
                return getProfiles(row)
                    .map((profile) => profile.name)
                    .join(' / ')
                    .toLocaleLowerCase();
            case 'outcome':
                return row.kind === 'agent' || row.kind === 'conflict' ? outcomeLabel(row) : labels[row.kind];
            case 'resolvedBy':
                if (row.kind !== 'agent') return row.actorName || 'Not recorded';
                return row.status === 'complete' && row.outcome !== 'unsure' ? 'Agent' : '—';
            case 'reviewedBy':
                return row.kind === 'agent' ? row.actorName || 'Not recorded' : '—';
            case 'date':
                return Number(row.createdAt);
        }
    };

const detailStyles = css`
    .agent-findings {
        margin-bottom: 20px;
        white-space: pre-wrap;
    }
    .profile-comparison {
        display: flex;
        height: 65vh;
    }
    .audit-action {
        margin: 8px 0;
    }
`;

const listStyles = css`
    .agent-status {
        display: flex;
        align-items: center;
        gap: 4px;
    }
    .agent-action-button {
        opacity: 0;
    }
    tr:hover .agent-action-button,
    tr:focus-within .agent-action-button {
        opacity: 1;
    }
`;

/** Combines unresolved conflicts, agent work awaiting review, and completed action history. */
export const ProfileConflicts: React.FC<{
    view: ProfileConflictActivity['section'];
    personId1?: string;
    personId2?: string;
}> = (props) => {
    const theme = useTheme();
    const { onOpen: openAgent, onSelectConversation } = useAgent();
    const { setList } = useSlides();
    const { setAlert } = useModal();
    const { setSnackbar } = useSnackbar();
    const { view } = props;
    const [page, setPage] = React.useState(0);
    const [userId, setUserId] = React.useState('');
    const [selected, setSelected] = React.useState<ProfileConflictActivity | null>(null);
    const [deepLinkOpen, setDeepLinkOpen] = React.useState(Boolean(props.personId1 && props.personId2));
    const [reviewError, setReviewError] = React.useState('');
    // Suppress repeat clicks until reload; the worker handles durable deduplication.
    const [requestedConflicts, setRequestedConflicts] = React.useState<string[]>([]);
    const [agentMenu, setAgentMenu] = React.useState<{ anchor: HTMLElement; row: ProfileConflictActivity } | null>(
        null
    );

    const { data, loading, error, refetch } = useQuery<{
        records: ProfileConflictActivity[];
        total: { aggregate: { count: number } };
    }>(ProfileConflictActivityQuery, {
        pollInterval: view === 'needs-resolution' || view === 'processing' ? pollIntervalMs : 0,
        variables: {
            includeDetails: view !== 'history',
            // Load complete work queues so DataPanel sorts and filters before paginating.
            // History keeps its server pagination and deferred record enrichment.
            limit: view === 'history' ? pageSize : null,
            offset: view === 'history' ? page * pageSize : 0,
            where: {
                section: { _eq: view },
                ...(view === 'history'
                    ? {
                          _or: [{ actorId: { _is_null: true } }, { actorId: { _neq: config.SystemUserId } }],
                          ...(userId ? { actorId: { _eq: userId } } : {})
                      }
                    : {})
            }
        }
    });
    const history = useQuery<{ records: ProfileConflictActivity[] }>(ProfileConflictHistoryRecords, {
        skip: view !== 'history' || !data?.records.length,
        variables: { ids: data?.records.map((row) => row.id) || [], limit: pageSize }
    });
    const users = useQuery<{ users: Array<Pick<ProfileConflictActivity, 'actorId' | 'actorName'>> }>(
        ProfileConflictActivityUsers,
        { variables: { systemUserId: config.SystemUserId }, skip: view !== 'history' }
    );
    const details = useQuery<{ records: ConflictDetails[] }>(ProfileConflictActivityDetails, {
        fetchPolicy: 'cache-and-network',
        skip: !selected && !deepLinkOpen,
        variables: {
            where: selected
                ? selected.kind === 'conflict'
                    ? { kind: { _eq: 'conflict' }, conflictId: { _eq: selected.conflictId } }
                    : { id: { _eq: selected.id } }
                : {
                      kind: { _eq: 'conflict' },
                      personId1: { _in: [props.personId1, props.personId2] },
                      personId2: { _in: [props.personId1, props.personId2] }
                  }
        }
    });
    const [markReviewed, { loading: reviewing }] = useMutation(MarkProfileConflictReviewed);
    const detail = details.data?.records[0];
    const total = data?.total.aggregate.count;

    React.useEffect(() => {
        if (detail?.kind === 'conflict') details.startPolling(pollIntervalMs);
        else details.stopPolling();
        return details.stopPolling;
    }, [detail?.kind, details.startPolling, details.stopPolling]);

    React.useEffect(() => {
        if (loading || total === undefined) return;
        const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
        if (page > lastPage) setPage(lastPage);
    }, [loading, total, page]);

    React.useEffect(() => {
        setPage(0);
        setUserId('');
    }, [view]);
    const handleUser = (event: React.ChangeEvent<{ value: unknown }>) => {
        setUserId(event.target.value as string);
        setPage(0);
    };
    const handlePage = (_: unknown, value: number) => setPage(value);
    const handleSelect = (row: ProfileConflictActivity) => () => {
        setReviewError('');
        setSelected(row);
    };
    const handleClose = () => {
        if (reviewing) return;
        setSelected(null);
        setDeepLinkOpen(false);
        setReviewError('');
    };
    const handleReviewed = async () => {
        try {
            await markReviewed({ variables: { id: detail.reviewId, reviewedAt: Date.now() } });
            setSelected(null);
            setDeepLinkOpen(false);
            await refetch();
        } catch (err) {
            setReviewError(err.message);
        }
    };
    const handleResolved = () => {
        if (detail.reviewId) {
            // Resolving an investigated conflict also acknowledges the agent's work.
            handleReviewed();
        } else {
            handleClose();
            refetch();
        }
    };
    const handleOpenConversation = (conversationId: string) => (event: React.MouseEvent) => {
        event.preventDefault();
        // Release the dialog's focus trap before opening the separate agent panel.
        handleClose();
        onSelectConversation(conversationId);
        openAgent();
    };
    const handleOpenPerson = (personId: string) => (event: React.MouseEvent) => {
        event.preventDefault();
        handleClose();
        const person = { personId };
        setList([person], person);
    };
    const handleOpenAgentMenu = (row: ProfileConflictActivity) => (event: React.MouseEvent<HTMLElement>) => {
        setAgentMenu({ anchor: event.currentTarget, row });
    };
    const handleCloseAgentMenu = () => setAgentMenu(null);
    const handleStartAgentInvestigation =
        ({ conflictId, reviewId }: ProfileConflictActivity) =>
        async () => {
            handleCloseAgentMenu();
            setRequestedConflicts((ids) => [...ids, conflictId]);
            setSnackbar('Agent investigation queued');
            try {
                const result = await startProfileConflictInvestigation(conflictId, reviewId);
                if (!result.success) throw new Error(result.data?.message || 'Unable to start agent investigation');
            } catch (err) {
                setRequestedConflicts((ids) => ids.filter((id) => id !== conflictId));
                setAlert('Unable to start agent investigation', err.message);
            }
        };
    const renderProfiles = (row: ProfileConflictActivity) => {
        const profiles = getProfiles(row).map((profile) => (
            <div key={profile.id}>
                <a href={`/person/${profile.id}`} onClick={handleOpenPerson(profile.id)}>
                    {profile.name}
                </a>
            </div>
        ));
        return <>{profiles}</>;
    };

    const getCellRenderer = {
        date: (value: CellValue) => <span>{fullDateTime(Number(value))}</span>,
        outcome: (value: CellValue, row: ProfileConflictActivity) => {
            const conversationButton = row.conversationId ? (
                <Tooltip title="Open in Orbit">
                    <IconButton
                        className="agent-action-button"
                        size="small"
                        aria-label="Open in Orbit"
                        onClick={handleOpenConversation(row.conversationId)}
                    >
                        <OpenInNew fontSize="small" />
                    </IconButton>
                </Tooltip>
            ) : null;
            const menuButton =
                (row.kind === 'conflict' && !row.reviewId) ||
                (row.section !== 'history' && row.status === 'complete' && row.outcome === 'unsure') ? (
                    <IconButton
                        className="agent-action-button"
                        size="small"
                        aria-label="Agent actions"
                        aria-haspopup="menu"
                        onClick={handleOpenAgentMenu(row)}
                    >
                        <ArrowDropDown />
                    </IconButton>
                ) : null;
            return (
                <div className="agent-status">
                    <div className="popover-link" onClick={handleSelect(row)}>
                        {value}
                    </div>
                    {conversationButton}
                    {menuButton}
                </div>
            );
        },
        profiles: (_: CellValue, row: ProfileConflictActivity) => renderProfiles(row)
    };
    const renderSummary = () => (
        <TablePagination
            component="div"
            count={total ?? 0}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            onChangePage={handlePage}
        />
    );
    const userOptions = users.data?.users.map((user) => (
        <MenuItem key={user.actorId} value={user.actorId}>
            {user.actorName}
        </MenuItem>
    ));
    const userSelector =
        view === 'history' ? (
            <Select
                value={userId}
                onChange={handleUser}
                variant="outlined"
                displayEmpty={true}
                inputProps={{ 'aria-label': 'Reviewed or resolved by' }}
            >
                <MenuItem value="">All users</MenuItem>
                {userOptions}
            </Select>
        ) : null;

    const renderAction = (action: ProfileConflictAction) => {
        const profiles = getProfiles(detail);
        const from = profiles.find((profile) => profile.id === action.personId1)?.name;
        const to = profiles.find((profile) => profile.id === action.personId2)?.name;
        const direction = action.kind === 'resolve' ? '' : [from, to].filter(Boolean).join(' → ');
        const value = action.contactValue || action.profileUrl || action.fileName;
        return (
            <div className="audit-action" key={`${action.kind}-${action.id}`}>
                {labels[action.kind]}
                {direction ? `: ${direction}` : ''}
                {value ? ` — ${value}` : ''}
            </div>
        );
    };
    const auditActions: ProfileConflictAction[] =
        detail?.kind === 'agent'
            ? [
                  ...(detail.review?.history || []),
                  ...(detail.review?.merges || []).map((action) => ({ ...action, kind: 'merge' as const }))
              ]
            : detail && detail.kind !== 'conflict'
              ? [{ ...detail, kind: detail.kind }]
              : [];
    const audit = auditActions.map(renderAction);
    const conversationLink = detail?.conversationId ? (
        <a href={`/orbit/chat/${detail.conversationId}`} onClick={handleOpenConversation(detail.conversationId)}>
            View agent conversation
        </a>
    ) : null;
    const reviewer = detail?.actorName ? (
        <Typography>
            {detail.kind === 'agent' ? 'Reviewed' : 'Action taken'} by {detail.actorName}
        </Typography>
    ) : null;
    const findings = detail ? (
        <>
            <Typography variant="h6" component="div">
                {renderProfiles(detail)}
            </Typography>
            <Typography>{outcomeLabel(detail)}</Typography>
            {reviewer}
            <div className="agent-findings">{detail.reason}</div>
            <Typography color="error">{detail.error}</Typography>
            {conversationLink}
            {audit}
        </>
    ) : null;
    const errorMessage = details.error || reviewError;
    const detailContent = errorMessage ? (
        <Typography color="error">{String(errorMessage)}</Typography>
    ) : details.loading && !detail ? (
        <CircularProgress />
    ) : detail ? (
        findings
    ) : (
        <Typography>This conflict is no longer open.</Typography>
    );
    const reviewButton =
        detail?.kind === 'agent' && detail.section === 'review' ? (
            <Button onClick={handleReviewed} disabled={reviewing}>
                {reviewing ? 'Saving...' : 'Mark reviewed'}
            </Button>
        ) : null;
    const needsResolution =
        detail?.kind === 'conflict' ||
        (detail?.section === 'review' && (detail.outcome === 'unsure' || detail.status === 'failed'));
    const dialogBody =
        needsResolution && !errorMessage ? (
            <ProfileConflictResolver
                key={detail.conflictId}
                personId1={detail.personId1}
                personId2={detail.personId2}
                readOnly={detail.status === 'pending'}
                onClose={handleClose}
                onResolved={handleResolved}
            >
                {findings}
            </ProfileConflictResolver>
        ) : (
            <>
                <DialogContent dividers={true}>{detailContent}</DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={reviewing}>
                        Close
                    </Button>
                    {reviewButton}
                </DialogActions>
            </>
        );
    const dialog =
        selected || deepLinkOpen ? (
            <Dialog open={true} onClose={handleClose} fullWidth={true} maxWidth="xl" css={detailStyles}>
                <DialogTitle>
                    <Typography variant="h4" component="div">
                        Profile conflict
                    </Typography>
                </DialogTitle>
                {dialogBody}
            </Dialog>
        ) : null;
    const columns: Array<DataPanelColumn<Column>> = [
        { id: 'profiles', label: 'Profiles' },
        { id: 'outcome', label: view === 'history' ? 'Action' : 'Agent status' },
        { id: 'resolvedBy', label: 'Resolved by' },
        { id: 'reviewedBy', label: 'Reviewed by' },
        { id: 'date', label: view === 'history' ? 'Recorded' : view === 'review' ? 'Completed' : 'Detected' }
    ];
    const listColumns =
        view === 'history'
            ? columns
            : columns.filter((column) => column.id !== 'resolvedBy' && column.id !== 'reviewedBy');
    const listError = error || (view === 'history' && history.error);
    const records = view === 'history' && data?.records.length ? history.data?.records : data?.records;
    const listLoading = loading || (view === 'history' && history.loading);
    const list = listError ? (
        <Typography color="error">{listError.message}</Typography>
    ) : (
        <DataPanel
            key={view}
            columns={listColumns}
            data={listLoading ? undefined : records || []}
            getCellValue={getCellValue}
            getCellRenderer={getCellRenderer}
            sortableColumns={view === 'history' ? [] : ['profiles', 'outcome', 'date']}
            filterableColumns={view === 'history' ? [] : ['outcome']}
            initialSortColumn="date"
            initialSortDirection="desc"
            pageSize={pageSize}
            hidePagination={view === 'history'}
            renderSummary={view === 'history' ? renderSummary : undefined}
        />
    );
    const viewSelector = <NavigationSelect routes={viewRoutes} />;
    const agentActionMenu = agentMenu ? (
        <Menu open={true} anchorEl={agentMenu.anchor} onClose={handleCloseAgentMenu}>
            <MenuItem
                dense={true}
                disabled={requestedConflicts.includes(agentMenu.row.conflictId)}
                onClick={handleStartAgentInvestigation(agentMenu.row)}
            >
                {agentMenu.row.reviewId ? 'Run agent again' : 'Run agent'}
            </MenuItem>
        </Menu>
    ) : null;
    const panel = (
        <div css={dataPanelContainerStyles(theme)}>
            <div className="selectors">
                <div css={selectorStyles}>
                    {viewSelector}
                    {userSelector}
                </div>
            </div>
            <div className="data-panel" css={listStyles}>
                {list}
            </div>
            {agentActionMenu}
            {dialog}
        </div>
    );
    return (
        <DocumentTitle title="Profile Conflicts">
            <div id="container">
                <Header title="Profile Conflicts" actions={[]} />
                {panel}
            </div>
        </DocumentTitle>
    );
};
